import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from '../Helmet';

const testimonials = [
  {
    id: 1,
    name: "Sophie D.",
    position: "Directrice de l'Innovation, Groupe de Luxe",
    image: "/testi-sophie.webp",
    quote: "Grâce aux formations d'UPNov IA, notre équipe a pu mettre en place une stratégie IA cohérente qui a transformé notre approche business. Le ROI a été immédiat !",
    rating: 5
  },
  {
    id: 2,
    name: "Marc L.",
    position: "DSI, Entreprise de Services Numériques",
    image: "/testi-marc.webp",
    quote: "La qualité des formateurs et l'approche pratique nous ont permis d'implémenter rapidement des solutions d'IA performantes. Nos équipes sont désormais autonomes.",
    rating: 5
  },
  {
    id: 3,
    name: "Nathalie P.",
    position: "DRH, Groupe Industriel",
    image: "/testi-nathalie.webp",
    quote: "La formation sur mesure a parfaitement répondu à nos attentes. L'accent mis sur l'éthique et la responsabilité dans l'usage de l'IA nous a particulièrement séduits.",
    rating: 5
  }
];

const renderStars = (rating: number) => {
  return Array.from({ length: 5 }).map((_, i) => (
    <Star
      key={i}
      className={`h-5 w-5 ${i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
    />
  ));
};

const reviewSchema = {
  '@context': 'https://schema.org',
  '@type': 'EducationalOrganization',
  name: 'Upnov IA',
  url: 'https://www.upnov.com',
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '5',
    reviewCount: String(testimonials.length),
    bestRating: '5',
    worstRating: '1',
  },
  review: testimonials.map((t) => ({
    '@type': 'Review',
    author: { '@type': 'Person', name: t.name },
    reviewRating: {
      '@type': 'Rating',
      ratingValue: String(t.rating),
      bestRating: '5',
    },
    reviewBody: t.quote,
  })),
};

const Testimonials = () => {
  return (
    <>
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(reviewSchema)}</script>
    </Helmet>
    <section className="bg-white section-padding">
            <div className="container-custom">
      <div>
       <div className="mt-8 mb-16 md:mb-24 p-4 bg-gray-50 rounded-lg">

          <p className="text-sm text-gray-600 mb-4">
            <span className="font-semibold">Ils nous font confiance :</span>
          </p>

          <div className="relative overflow-hidden w-full">
            <div className="flex items-center gap-10 animate-marquee whitespace-nowrap w-full">
              {[
                'edf',
                'ratp',
                'orano',
                'loreal',
                'thales',
                'vinci',
                'eiffage',
                'CaissedesDepots',
                'laposte',
                'safran',
                'yvesRocher',
                'sncf',
                'sagem',
                'carrefour',
              ].map((logo) => (
                <img
                  key={logo}
                  src={`logo/logo-${logo}.webp`}
                  alt={`${logo} logo`}
                  width="140"
                  height="56"
                  className="h-12 sm:h-14 w-auto grayscale hover:grayscale-0 transition-all duration-300"
                />
              ))}

              {[
                'edf',
                'ratp',
                'orano',
                'loreal',
                'thales',
                'vinci',
                'eiffage',
                'CaissedesDepots',
                'laposte',
                'safran',
                'yvesRocher',
                'sncf',
                'sagem',
                'carrefour',
              ].map((logo) => (
                <img
                  key={`${logo}-clone`}
                  src={`logo/logo-${logo}.webp`}
                  alt={`${logo} logo`}
                  width="140"
                  height="56"
                  className="h-12 sm:h-14 w-auto grayscale hover:grayscale-0 transition-all duration-300"
                />
              ))}
            </div>
          </div>
        </div>
      </div>

        <div className="text-center mb-16">
          <h2 className="heading-lg mb-6">Ce que nos clients disent</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Découvrez les témoignages de professionnels et d'entreprises qui ont
            transformé leur approche grâce à nos formations en IA.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-gray-50 p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300"
            >
              <div className="flex items-center space-x-4 mb-6">
                <img
                  src={testimonial.image}
                  alt={testimonial.name}
                  width="64"
                  height="64"
                  className="w-16 h-16 rounded-full object-cover"
                />
                <div>
                  <h3 className="font-medium">{testimonial.name}</h3>
                  <p className="text-sm text-gray-500">{testimonial.position}</p>
                </div>
              </div>

              <div className="flex mb-4">
                {renderStars(testimonial.rating)}
              </div>

              <blockquote className="italic text-gray-600">
                "{testimonial.quote}"
              </blockquote>
            </motion.div>
          ))}
        </div>

        <div className="mt-16 p-8 bg-primary/5 rounded-xl">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="mb-6 md:mb-0 text-center md:text-left">
              <h3 className="text-2xl font-semibold mb-2">Prêt à former vos équipes ?</h3>
              <p className="text-gray-600">Contactez-nous pour découvrir comment nos formations peuvent bénéficier à votre entreprise.</p>
            </div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <Link to="/DemandeInfo/" className="btn-primary">
                Demander un devis gratuit
              </Link>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
    </>
  );
};

export default Testimonials;